import React, { useState } from "react";
import constants from "../../Constants";
import cookie from "react-cookies";
import moment from "moment";
import "./FilePreviewer.css";

const baseURL = constants.baseURL;
const Authorization = () => ({
  headers: {
    "Content-Type": "application/json",
    Authorization: cookie.load("session-id-qt"),
  },
});

const getPreviewType = (url) => {
  const urlLower = url.toLowerCase();
  
  // Images
  if (/\.(jpeg|jpg|gif|png|svg|webp|bmp|tiff|ico)$/i.test(urlLower)) 
    return "image";
  
  // PDFs
  if (/\.pdf$/i.test(urlLower)) 
    return "pdf";
  
  // Videos
  if (/\.(mp4|webm|mov|avi|wmv|flv|mkv|3gp)$/i.test(urlLower)) 
    return "video";
  
  // Audio
  if (/\.(mp3|wav|ogg|m4a|flac|aac)$/i.test(urlLower)) 
    return "audio";
  
  // Text/Code files
  if (/\.(txt|rtf|md|json|xml|csv|html|css|js|jsx|ts|tsx|py|java|c|cpp|php|rb|sh|yml|yaml)$/i.test(urlLower)) 
    return "text";
  
  // Office Documents
  if (/\.(doc|docx|xls|xlsx|ppt|pptx)$/i.test(urlLower)) 
    return "office";
  
  // Archives
  if (/\.(zip|rar|7z|tar|gz|bz2)$/i.test(urlLower)) 
    return "archive";

  return "unsupported";
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + " bytes";
  else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
  else return (bytes / 1048576).toFixed(1) + " MB";
};

const renderPreview = (url) => {
  const type = getPreviewType(url);
  
  switch (type) {
    case "image":
      return (
        <div className="preview-container">
          <img
            src={url}
            alt="Preview"
            style={{ maxWidth: "100%", maxHeight: "500px" }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "https://via.placeholder.com/400x300?text=Image+Load+Error";
            }}
          />
        </div>
      );
    case "pdf":
      return (
        <div className="preview-container">
          <object data={url} type="application/pdf" width="100%" height="500px">
            <iframe src={url} width="100%" height="500px" title="PDF preview">
              <p>
                This browser does not support PDFs. Please download the PDF to
                view it: <a href={url}>Download PDF</a>.
              </p>
            </iframe>
          </object>
        </div>
      );
    case "video":
      return (
        <div className="preview-container">
          <video controls width="100%" height="auto">
            <source src={url} />
            Your browser does not support the video tag.
          </video>
        </div>
      );
    case "audio":
      return (
        <div className="preview-container">
          <audio controls style={{ width: "100%" }}>
            <source src={url} />
            Your browser does not support the audio tag.
          </audio>
        </div>
      );
    case "text":
      return (
        <div className="preview-container document">
          <iframe 
            src={url} 
            title="Text file preview" 
            width="100%" 
            height="500px"
            style={{ border: "1px solid #eee" }}
          >
            <p>Your browser does not support iframes.</p>
          </iframe>
        </div>
      );
    case "office":
    case "archive":
      const iconClass = type === "office" 
        ? "mdi mdi-file-document-outline" 
        : "mdi mdi-zip-box-outline";
      const typeLabel = type === "office" ? "Office document" : "Archive file";
      
      return (
        <div className="preview-container document">
          <div className="document-preview">
            <i className={`${iconClass} document-icon`}></i>
            <p>{typeLabel} preview not available</p>
            <a href={url} className="download-btn" target="_blank" rel="noopener noreferrer">
              Download to view
            </a>
          </div>
        </div>
      );
    default:
      return (
        <div className="preview-container unsupported">
          <div className="unsupported-preview">
            <i className="mdi mdi-file-question-outline"></i>
            <p>File type not supported for preview</p>
            <a href={url} className="download-btn" target="_blank" rel="noopener noreferrer">
              Download File
            </a>
          </div>
        </div>
      );
  }
};

function FilePreviewer({ orderfile, getOrderFiles, getOrderConversations }) {
  const [openPreview, setOpenPreview] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  const deleteOrderFile = (orderid, fileid) => {
    setIsDeleting(true);
    setPreviewError(null);
    
    fetch(baseURL + "/customer-delete-order-file", {
      method: "POST",
      body: JSON.stringify({ orderid, key: orderfile.Key }),
      ...Authorization(),
    })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }
      return response.json();
    })
    .then(() => {
      addConversation(orderid, fileid);
      getOrderFiles(orderid);
      getOrderConversations(orderid);
      setShowDeleteConfirm(false);
    })
    .catch(error => {
      console.error("Error deleting file:", error);
      setPreviewError("Failed to delete file. Please try again.");
    })
    .finally(() => {
      setIsDeleting(false);
    });
  };

  const addConversation = (orderid, fileid) => {
    fetch(baseURL + "/customer-add-conversation", {
      method: "POST",
      body: JSON.stringify({ orderid, message: "Deleted a file :: " + fileid }),
      ...Authorization(),
    })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }
      return response.json();
    })
    .catch(error => {
      console.error("Error adding conversation:", error);
    });
  };

  const handleOpenPreview = () => {
    setIsPreviewLoading(true);
    setPreviewError(null);
    setOpenPreview(true);
    
    // Simulate checking if the file is accessible
    const img = new Image();
    img.onload = () => setIsPreviewLoading(false);
    img.onerror = () => {
      setIsPreviewLoading(false);
      // Only set error for images, other types will be handled by their respective elements
      if (getPreviewType(orderfile.Key) === "image") {
        setPreviewError("Unable to load file preview");
      }
    };
    
    // Start loading check for images
    if (getPreviewType(orderfile.Key) === "image") {
      img.src = `https://media.qiktruck.co.za/${orderfile.Key}`;
    } else {
      // For other file types, just remove loading state after a short delay
      setTimeout(() => setIsPreviewLoading(false), 500);
    }
  };

  const renderIcon = (key) => {
    const keyLower = key.toLowerCase();
    
    // PDF files
    if (/\.pdf$/i.test(keyLower))
      return <i className="mdi mdi-file-pdf-box" aria-hidden="true"></i>;
    
    // Image files
    if (/\.(jpeg|jpg|gif|png|webp|svg|bmp|tiff|ico)$/i.test(keyLower))
      return <i className="mdi mdi-file-image" aria-hidden="true"></i>;
    
    // Word documents
    if (/\.(doc|docx)$/i.test(keyLower))
      return <i className="mdi mdi-file-word" aria-hidden="true"></i>;
    
    // Excel spreadsheets
    if (/\.(xls|xlsx|csv)$/i.test(keyLower))
      return <i className="mdi mdi-file-excel" aria-hidden="true"></i>;
    
    // PowerPoint presentations
    if (/\.(ppt|pptx)$/i.test(keyLower))
      return <i className="mdi mdi-file-powerpoint" aria-hidden="true"></i>;
    
    // Archive files
    if (/\.(zip|rar|7z|tar|gz|bz2)$/i.test(keyLower))
      return <i className="mdi mdi-zip-box" aria-hidden="true"></i>;
    
    // Text files
    if (/\.(txt|rtf|md)$/i.test(keyLower))
      return <i className="mdi mdi-file-document-outline" aria-hidden="true"></i>;
    
    // Code files
    if (/\.(html|css|js|jsx|ts|tsx|py|java|c|cpp|php|rb|sh|json|xml|yml|yaml)$/i.test(keyLower))
      return <i className="mdi mdi-code-tags" aria-hidden="true"></i>;
    
    // Video files
    if (/\.(mp4|webm|mov|avi|wmv|flv|mkv|3gp)$/i.test(keyLower))
      return <i className="mdi mdi-file-video" aria-hidden="true"></i>;
    
    // Audio files
    if (/\.(mp3|wav|ogg|m4a|flac|aac)$/i.test(keyLower))
      return <i className="mdi mdi-file-music" aria-hidden="true"></i>;
    
    // Default file icon
    return <i className="mdi mdi-file-outline" aria-hidden="true"></i>;
  };

  const fileName = orderfile?.Key?.split("/")[2]?.split("<->")[1] || 
                  orderfile?.Key?.split("/")[2] || "File";
  
  const fileUrl = `https://media.qiktruck.co.za/${orderfile?.Key}`;

  return (
    <div className="file-item-container">
      <div className="file-item">
        <div className="file-icon" onClick={handleOpenPreview}>
          {renderIcon(orderfile?.Key)}
        </div>
        <div className="file-details" onClick={handleOpenPreview}>
          <div className="file-title-row">
            <span className="file-title">{orderfile?.Metadata?.title || fileName}</span>
            <span className="file-date">{moment(orderfile?.LastModified).fromNow()}</span>
          </div>
          <div className="file-name">{fileName}</div>
          <div className="file-size">{formatFileSize(orderfile?.Size || 0)}</div>
        </div>
        <div className="file-actions">
          <a
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="file-action-btn"
            title="Download"
          >
            <i className="mdi mdi-download" aria-hidden="true"></i>
          </a>
          <button
            className="file-action-btn preview-btn"
            onClick={handleOpenPreview}
            title="Preview"
          >
            <i className="mdi mdi-eye" aria-hidden="true"></i>
          </button>
          <button
            className="file-action-btn delete-btn"
            onClick={() => setShowDeleteConfirm(true)}
            title="Delete"
          >
            <i className="mdi mdi-delete" aria-hidden="true"></i>
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="delete-modal">
          <div className="delete-modal-content">
            <h3>Confirm Delete</h3>
            <p>Are you sure you want to delete this file?</p>
            <p className="file-to-delete">{fileName}</p>
            {previewError && <p className="error-message">{previewError}</p>}
            <div className="delete-modal-actions">
              <button 
                className="cancel-btn" 
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setPreviewError(null);
                }}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button 
                className="confirm-delete-btn" 
                onClick={() => deleteOrderFile(orderfile.orderid, orderfile.Key)}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <i className="mdi mdi-loading mdi-spin" style={{ marginRight: '8px' }}></i>
                    Deleting...
                  </>
                ) : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {openPreview && (
        <div className="preview-modal">
          <div className="preview-modal-content">
            <div className="preview-header">
              <h3>{orderfile?.Metadata?.title || fileName}</h3>
              <button 
                className="close-preview-btn" 
                onClick={() => {
                  setOpenPreview(false);
                  setPreviewError(null);
                }}
              >
                <i className="mdi mdi-close"></i>
              </button>
            </div>
            <div className="preview-file-info">
              <span className="preview-file-name">{fileName}</span>
              <span className="preview-file-size">{formatFileSize(orderfile?.Size || 0)}</span>
              <span className="preview-file-date">
                Last modified: {moment(orderfile?.LastModified).format("MMM D, YYYY [at] h:mm A")}
              </span>
            </div>
            <div className="preview-content">
              {isPreviewLoading ? (
                <div className="loading-container">
                  <i className="mdi mdi-loading mdi-spin loading-icon"></i>
                  <p>Loading preview...</p>
                </div>
              ) : previewError ? (
                <div className="error-container">
                  <i className="mdi mdi-alert-circle error-icon"></i>
                  <p>{previewError}</p>
                  <a href={fileUrl} className="download-btn" target="_blank" rel="noopener noreferrer">
                    Download Instead
                  </a>
                </div>
              ) : (
                renderPreview(fileUrl)
              )}
            </div>
            <div className="preview-actions">
              <a
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="download-btn"
              >
                <i className="mdi mdi-download"></i> Download
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FilePreviewer;
